import type { DriftStatus, ScenarioStatus, VendorId } from '@/types/domain';
import type { ScenarioId, ScenarioVersionId } from '@/types/ids';

/**
 * ScenariosApi — typed client for the Scenario Catalog screen (BC-01).
 *
 * Implementations:
 *   - api.mock.ts  — MSW handlers + `createMockScenariosApi()` for unit tests.
 *   - (future)     — `openapi-fetch` client hitting `sentinel-api`.
 *
 * BRD §24.2 names the catalog columns surfaced by `listScenarios`. The
 * version list comes from BC-01's `GetScenario` + `GetActiveVersion`
 * queries; we split those into three endpoints so the screen can lazy-load
 * a version history when a row is expanded.
 */

export interface ScenarioRow {
  scenarioId: ScenarioId;
  /** Human-readable canonical scenario name (BRD §24.2.1). */
  name: string;
  /** Vendor source platform (BRD §24.2.2). */
  sourcePlatform: VendorId;
  /** Vendor-side scenario id before canonicalisation (BRD §24.2.3). */
  sourceScenarioId: string;
  /** Semver-ish tag of the latest published version (BRD §24.2.4). */
  latestVersion: string;
  /** Lifecycle status (BRD §24.2.5). */
  status: ScenarioStatus;
  /** ISO-8601 timestamp of last replay touching this scenario (BRD §24.2.6). */
  lastReplayAt: string | null;
  /** Drift posture (BRD §24.2.7). */
  driftStatus: DriftStatus;
  /** Owner display name (BRD §24.2.8). */
  owner: string;
  /** Scenario family / typology bucket — shown as a sub-label on the name. */
  family: string;
}

export interface ScenarioVersionRow {
  versionId: ScenarioVersionId;
  scenarioId: ScenarioId;
  version: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  normalizedLogicHash: string;
  status: ScenarioStatus;
}

export interface ScenariosQuery {
  /** Filter by status; `undefined` means "any". */
  status?: ScenarioStatus;
  /** Filter by vendor source platform; `undefined` means "any". */
  sourcePlatform?: VendorId;
  /** Full-text search (future — accepted by the client for now). */
  q?: string;
}

export interface ScenariosApi {
  listScenarios(query: ScenariosQuery): Promise<ScenarioRow[]>;
  getScenario(id: ScenarioId): Promise<ScenarioRow | null>;
  listVersions(id: ScenarioId): Promise<ScenarioVersionRow[]>;
}

/** REST endpoint paths — used by MSW handlers and the future real client. */
export const SCENARIOS_ENDPOINTS = {
  list: '/api/v1/scenarios',
  byId: (id: string) => `/api/v1/scenarios/${id}`,
  versions: (id: string) => `/api/v1/scenarios/${id}/versions`,
} as const;
